import { EMAIL_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { useCallback } from "react";

import { isUserWithPendingInvitation } from "../helpers";
import {
  EMAIL_VERIFICATION_ACTION_TYPES,
  type EmailVerificationAction,
  type EmailVerificationState,
  type EmailVerificationStatus,
} from "../types";
import type { useEmailVerificationMutations } from "./useEmailVerificationMutations";

interface UseEmailVerificationHandlersParams {
  dispatch: React.Dispatch<EmailVerificationAction>;
  mutations: ReturnType<typeof useEmailVerificationMutations>;
  setValue: (name: string, value: string) => void;
  state: EmailVerificationState;
  validateEmail: (
    email: string,
  ) => Promise<{ status: EmailVerificationStatus }>;
}

export const useEmailVerificationHandlers = ({
  state,
  dispatch,
  setValue,
  mutations,
  validateEmail,
}: UseEmailVerificationHandlersParams) => {
  const {
    verifyOTPMutation,
    resendOTPMutation,
    signInWithPasswordMutation,
    forgotPasswordMutation,
  } = mutations;

  const handleEmailChange = useCallback(
    (email: string) => {
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.SET_EMAIL,
        payload: email,
      });
    },
    [dispatch],
  );

  const startValidation = useCallback(() => {
    dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.START_VALIDATION });
  }, [dispatch]);

  const handleValidationSuccess = useCallback(
    (status: EmailVerificationStatus) => {
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_SUCCESS,
        payload: { status },
      });
    },
    [dispatch],
  );

  const handleValidationError = useCallback(
    (errorMessage: string) => {
      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_ERROR,
        payload: errorMessage,
      });
    },
    [dispatch],
  );

  const handleOTPSubmit = useCallback(
    (otp: string) => {
      if (!state.email) return;

      dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.START_OTP_SUBMISSION });
      verifyOTPMutation.mutate({ email: state.email, otp });
    },
    [state.email, dispatch, verifyOTPMutation],
  );

  const handlePasswordSubmit = useCallback(
    (password: string) => {
      if (!state.email) return;

      dispatch({
        type: EMAIL_VERIFICATION_ACTION_TYPES.START_PASSWORD_SUBMISSION,
      });
      signInWithPasswordMutation.mutate({ email: state.email, password });
    },
    [state.email, dispatch, signInWithPasswordMutation],
  );

  const handleForgotPasswordSubmit = useCallback(() => {
    if (!state.email) return;

    dispatch({
      type: EMAIL_VERIFICATION_ACTION_TYPES.START_FORGOT_PASSWORD_SUBMISSION,
    });
    forgotPasswordMutation.mutate(state.email);
  }, [state.email, dispatch, forgotPasswordMutation]);

  const handleResendOTP = useCallback(() => {
    if (!state.email) return;

    dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.START_RESEND_OTP });
    resendOTPMutation.mutate(state.email);
  }, [state.email, dispatch, resendOTPMutation]);

  const showOTPForm = useCallback(() => {
    dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.SHOW_OTP_FORM });
  }, [dispatch]);

  const showPasswordForm = useCallback(() => {
    dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.SHOW_PASSWORD_FORM });
  }, [dispatch]);

  const showClaimForm = useCallback(() => {
    dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.SHOW_CLAIM_FORM });
    if (state.verifiedOTP && isUserWithPendingInvitation(state.status)) {
      setValue("otp", state.verifiedOTP);
    }
  }, [dispatch, state.verifiedOTP, state.status, setValue]);

  const resetToInitial = useCallback(() => {
    dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.RESET_TO_INITIAL });
    setValue("password", "");
    setValue("otp", "");
  }, [dispatch, setValue]);

  const handleAuthenticationLinkClick = useCallback(() => {
    if (!state.email) return;

    if (!EMAIL_REGEX.test(state.email)) return;

    void validateEmail(state.email).catch(() => {});
  }, [state.email, validateEmail]);

  return {
    handleEmailChange,
    startValidation,
    handleValidationSuccess,
    handleValidationError,
    handleOTPSubmit,
    handlePasswordSubmit,
    handleForgotPasswordSubmit,
    handleResendOTP,
    showOTPForm,
    showPasswordForm,
    showClaimForm,
    resetToInitial,
    handleAuthenticationLinkClick,
  };
};
